/**
 * Shared date-range model for the dashboard/session filters.
 *
 * All dates are handled as local `YYYY-MM-DD` strings — the format the API's
 * `start_date`/`end_date` params and `<input type="date">` both expect. Note
 * `toISOString().slice(0, 10)` is deliberately NOT used: it converts to UTC
 * first, which rolls the date forward an entire day for anyone west of GMT in
 * the evening (so "last 7 days" silently asked for tomorrow).
 */

export type RangePreset = "7d" | "30d" | "90d" | "mtd" | "ytd" | "custom";

export interface DateRange {
  startDate: string;
  endDate: string;
  preset: RangePreset;
}

export const RANGE_PRESETS: { key: Exclude<RangePreset, "custom">; label: string }[] = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "mtd", label: "MTD" },
  { key: "ytd", label: "YTD" },
];

/** Local-timezone `YYYY-MM-DD` for a Date. */
export function toDayString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parse a `YYYY-MM-DD` day string as a *local* midnight Date. */
export function fromDayString(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function presetRange(preset: Exclude<RangePreset, "custom">): DateRange {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case "7d":
      // Inclusive of today, so 7 days total = today minus 6.
      start.setDate(end.getDate() - 6);
      break;
    case "30d":
      start.setDate(end.getDate() - 29);
      break;
    case "90d":
      start.setDate(end.getDate() - 89);
      break;
    case "mtd":
      start.setDate(1);
      break;
    case "ytd":
      start.setMonth(0, 1);
      break;
  }
  return { startDate: toDayString(start), endDate: toDayString(end), preset };
}

export function defaultRange(): DateRange {
  return presetRange("7d");
}

/** Human label, e.g. "Jul 27 – Aug 2, 2026". */
export function rangeLabel(range: DateRange): string {
  const start = fromDayString(range.startDate);
  const end = fromDayString(range.endDate);
  if (!start || !end) return "All dates";
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  })} – ${end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}

/** Inclusive day count in the range (1 when start === end). */
export function rangeDays(range: DateRange): number | null {
  const start = fromDayString(range.startDate);
  const end = fromDayString(range.endDate);
  if (!start || !end) return null;
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
}
